import React,  { useState, useEffect, useRef, useMemo } from "react";
import "../../App.css";
import norecords from "../../assests/norecords.gif";
import CustomButton from "../Buttons/CustomButton";
import CreateDatabase from "../DBMigration/CreateDatabase";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import CustomTooltip from "../Buttons/CustomTooltip";

const cellStyle = {
  textAlign: "left",
  verticalAlign: "middle",
};

function ListOfMigrationTable({
  setShowEdit,
  onEditJob,
  setShow,
  setSelectedJob,
  refreshKey,
  setRefreshKey,
  onLogClick,
  onViewClick,
  onViewProgress,
  setShowCreateDatabase,
  forceTooltipJobId,
  tooltipVisible,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const [expandedRows, setExpandedRows] = useState({});
  const [checkedRows, setCheckedRows] = useState({});
  const [indeterminateStates, setIndeterminateStates] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [showCreateDatabase, setShowCreateDatabase] = useState(false);
  const [databaseStep, setDatabaseStep] = useState(1);
  // Now the useEffect can safely use setDatabaseStep
  useEffect(() => {
    if (location.state?.openCreateDatabase) {
      setShowCreateDatabase(true);
      setDatabaseStep(location.state.databaseStep || 1);

      // Clear the navigation state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    } else {
      // If no navigation state (like on refresh), ensure modal is closed
      setShowCreateDatabase(false);
    }
  }, [location.state, setShowCreateDatabase, setDatabaseStep]);

  const headerCheckboxRef = useRef(null);
  const jobCheckboxRefs = useRef({});
  const socketRef = useRef(null);
  const retryDelayRef = useRef(1000);
  const maxRetryDelay = 30000;

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        row.job_name?.toLowerCase().includes(query) ||
        row.description?.toLowerCase().includes(query) ||
        row.source_db?.toLowerCase().includes(query) ||
        row.target_db?.toLowerCase().includes(query) ||
        row.status?.toLowerCase().includes(query) ||
        row.tables?.some((table) =>
          table.table_name?.toLowerCase().includes(query)
        )
      );
    });
  }, [data, searchQuery]);

  // Add this function inside the ListOfMigrationTable component
  const getPlatformDisplayName = (dbName) => {
    if (!dbName) return "N/A";

    const platformMap = {
      oracle: "Oracle",
      postgresql: "PostgreSQL",
      snowflake: "Snowflake",
      redshift: "Redshift",
      databricks: "Databricks",
      bigquery: "BigQuery",
      sqlserver: "SQL Server",
      mysql: "MySQL",
    };

    return platformMap[dbName.toLowerCase()] || dbName;
  };
  // WebSocket connection
  useEffect(() => {
    const connectWebSocket = () => {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) {
        setError("Authentication token missing");
        setLoading(false);
        return;
      }
const wsBaseJobs = process.env.REACT_APP_API_URL_DATA_MIGRATION
      socketRef.current = new WebSocket(
        `${wsBaseJobs}/ws/migration_jobs_list?token=${authToken}`
      );

      socketRef.current.onopen = () => {
        setError(null);
        setLoading(false);
        retryDelayRef.current = 1000;
      };

      socketRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          if (message.event === "initial_load" || message.event === "update") {
            const processedJobs = (message.data.migration_jobs || []).map(
              (job) => ({
                ...job,

                job_id: Number(job.job_id),
                started_at: job.started_at || "N/A",
                completed_at: job.completed_at || "N/A",
                tables: (job.tables || []).map((table) => ({
                  ...table,
                  table_id: Number(table.table_id),
                  table_created_at: table.table_created_at || "N/A",
                  table_completed_at: table.table_completed_at || "N/A",
                })),
              })
            );
            setData((prev) => {
              if (JSON.stringify(prev) !== JSON.stringify(processedJobs)) {
                return processedJobs;
              }
              return prev;
            });
          }
        } catch (err) {
          console.error("WebSocket message parsing error:", err);
        }
      };

      socketRef.current.onclose = (event) => {
        if (!event.wasClean) {
          console.warn("WebSocket closed unexpectedly:", event);
        }
        if (!document.hidden) {
          if (retryDelayRef.current >= maxRetryDelay) {
            setError("Connection lost. Retrying...");
          }
          setTimeout(connectWebSocket, retryDelayRef.current);
          retryDelayRef.current = Math.min(
            retryDelayRef.current * 2,
            maxRetryDelay
          );
        }
      };

      socketRef.current.onerror = (err) => {
        console.error("WebSocket error:", err);
        socketRef.current?.close();
      };
    };

    const handleVisibilityChange = () => {
      if (document.hidden && socketRef.current) {
        socketRef.current.close();
      } else if (
        !document.hidden &&
        (!socketRef.current ||
          socketRef.current.readyState === WebSocket.CLOSED)
      ) {
        connectWebSocket();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    connectWebSocket();

    return () => {
      if (socketRef.current) socketRef.current.close();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Checkbox state management
  useEffect(() => {
    const calculateIndeterminateStates = () => {
      const newIndeterminateStates = {};

      // Count total items and checked items
      const totalItems =
        filteredData.length +
        filteredData.reduce((sum, row) => sum + (row.tables?.length || 0), 0);
      const checkedItems = Object.keys(checkedRows).filter(
        (key) => checkedRows[key]
      ).length;

      // Header checkbox state
      newIndeterminateStates.header =
        checkedItems > 0 && checkedItems < totalItems;

      // Job checkboxes states
      filteredData.forEach((row) => {
        const jobKey = `job-${row.job_id}`;
        if (row.tables?.length > 0) {
          const tableKeys = row.tables.map((t) => `table-${t.table_id}`);
          const checkedTables = tableKeys.filter((k) => checkedRows[k]).length;
          newIndeterminateStates[jobKey] =
            checkedTables > 0 && checkedTables < tableKeys.length;
        }
      });

      return newIndeterminateStates;
    };

    const newIndeterminateStates = calculateIndeterminateStates();

    // Only update state if something actually changed
    if (
      JSON.stringify(newIndeterminateStates) !==
      JSON.stringify(indeterminateStates)
    ) {
      setIndeterminateStates(newIndeterminateStates);
    }

    // Update header checkbox
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate =
        newIndeterminateStates.header || false;
    }

    // Update job checkboxes
    filteredData.forEach((row) => {
      const jobKey = `job-${row.job_id}`;
      const checkbox = jobCheckboxRefs.current[jobKey];
      if (checkbox) {
        checkbox.indeterminate = newIndeterminateStates[jobKey] || false;
      }
    });
  }, [checkedRows, filteredData, indeterminateStates]);

  const handleSelectAllChange = () => {
    const allChecked =
      filteredData.length > 0 &&
      filteredData.every((row) => checkedRows[`job-${row.job_id}`]) &&
      filteredData.every((row) =>
        row.tables?.every((table) => checkedRows[`table-${table.table_id}`])
      );

    setCheckedRows((prev) => {
      const newChecked = { ...prev };
      if (allChecked) {
        // Uncheck all
        filteredData.forEach((row) => {
          delete newChecked[`job-${row.job_id}`];
          row.tables?.forEach((table) => {
            delete newChecked[`table-${table.table_id}`];
          });
        });
      } else {
        // Check all
        filteredData.forEach((row) => {
          newChecked[`job-${row.job_id}`] = true;
          row.tables?.forEach((table) => {
            newChecked[`table-${table.table_id}`] = true;
          });
        });
      }
      return newChecked;
    });
  };

  const handleCheckboxChange = (id, isTable = false, jobId = null) => {
    const prefix = isTable ? "table-" : "job-";
    const key = `${prefix}${id}`;
    const jobKey = jobId !== null ? `job-${jobId}` : null;

    setCheckedRows((prev) => {
      const newChecked = { ...prev };
      newChecked[key] = !prev[key];

      if (isTable && jobKey) {
        const job = data.find((j) => j.job_id === jobId);
        if (job?.tables) {
          const tableKeys = job.tables.map((t) => `table-${t.table_id}`);
          const allTablesChecked = tableKeys.every((k) => newChecked[k]);
          const anyTablesChecked = tableKeys.some((k) => newChecked[k]);

          if (anyTablesChecked) {
            newChecked[jobKey] = allTablesChecked;
          } else {
            delete newChecked[jobKey];
          }
        }
      } else {
        const job = data.find((j) => j.job_id === id);
        job?.tables?.forEach((table) => {
          const tableKey = `table-${table.table_id}`;
          if (newChecked[key]) {
            newChecked[tableKey] = true;
          } else {
            delete newChecked[tableKey];
          }
        });
      }

      // Clean up any undefined values
      Object.keys(newChecked).forEach((k) => {
        if (newChecked[k] === undefined) {
          delete newChecked[k];
        }
      });

      return newChecked;
    });
  };

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const getSelectedJob = () => {
    const selectedJobId = Object.keys(checkedRows)
      .find((key) => key.startsWith("job-") && checkedRows[key])
      ?.replace("job-", "");

    if (selectedJobId) {
      return data.find((job) => job.job_id === parseInt(selectedJobId));
    }
    return null;
  };

  const hasSelectedJob = () => {
    const selectedJobKeys = Object.keys(checkedRows).filter(
      (key) => key.startsWith("job-") && checkedRows[key]
    );
    return selectedJobKeys.length === 1;
  };

  const TruncatedText = ({ text, maxLength = 18, jobId }) => {
    if (!text) return "N/A";

    const isTruncated = text.length > maxLength;
    const truncated = isTruncated ? `${text.substring(0, maxLength)}...` : text;

    return isTruncated || forceTooltipJobId === jobId ? (
      <CustomTooltip
        content={text}
        placement="right"
        fallbackPlacements={["top"]}
        forceShow={forceTooltipJobId === jobId && tooltipVisible}
      >
        <span style={{ cursor: "pointer" }}>{truncated}</span>
      </CustomTooltip>
    ) : (
      <span>{text}</span>
    );
  };
  const allChecked =
    filteredData.length > 0 &&
    filteredData.every((row) => checkedRows[`job-${row.job_id}`]) &&
    filteredData.every((row) =>
      row.tables?.every((table) => checkedRows[`table-${table.table_id}`])
    );

  if (loading) {
    return (
      <div className="migration-table-container">
        {/* Skeleton loader */}
        <div className="d-flex justify-content-between mb-3">
          <div
            className="loadingStyle"
            style={{ width: "300px", height: "40px" }}
          ></div>
          <div
            className="loadingStyle"
            style={{ width: "200px", height: "40px" }}
          ></div>
        </div>

        <div className="d-flex mb-3">
          <div
            className="loadingStyle"
            style={{ width: "140px", height: "30px", marginRight: "10px" }}
          ></div>
        </div>

        <table className="table">
          <thead>
            <tr>
              {[...Array(8)].map((_, i) => (
                <th key={i}>
                  <div
                    className="loadingStyle"
                    style={{ width: "80%", height: "20px" }}
                  ></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, rowIndex) => (
              <tr key={rowIndex}>
                {[...Array(8)].map((_, cellIndex) => (
                  <td key={cellIndex}>
                    <div
                      className="loadingStyle"
                      style={{
                        width:
                          cellIndex === 0
                            ? "20px"
                            : `${80 + Math.random() * 20}%`,
                        height: "16px",
                        margin: cellIndex === 0 ? "0 auto" : "0",
                      }}
                    ></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (error) {
    return <div className="text-center mt-3 text-danger">Error: {error}</div>;
  }

  const handleEditClick = () => {
    if (hasSelectedJob()) {
      const selectedJob = getSelectedJob(); // Get from checkedRows + data
      if (selectedJob) {
        onEditJob(selectedJob); // Updates parent state (DBMigration)
        setShowEdit(true);
      }
    }
  };

  const handleLogClick = (jobId, jobName, tableId = null, tableName = null) => {
    if (tableId) {
      console.log(
        `Viewing logs for table ${tableName} (ID: ${tableId}) in job ${jobName} (ID: ${jobId})`
      );
      // Handle table logs navigation or modal
    } else {
      console.log(`Viewing logs for job ${jobName} (ID: ${jobId})`);
      // Handle job logs navigation or modal
    }
  };
  const renderActions = (row, isTable = false, table = null) => {
    const itemKey = isTable ? `table-${table.table_id}` : `job-${row.job_id}`;
    const isSelected = checkedRows[itemKey] || false;
    const status = (isTable ? table.status : row.status)?.toLowerCase();
    const isInProgressOrFailed =
      status === "in progress" || status === "failed";

    return (
      <div className="dropdown d-sm-inline-block">
        <i
          className="fas fa-ellipsis-v cursor-pointer"
          data-bs-toggle="dropdown"
        ></i>
        <div className="dropdown-menu dropdown-menu-end min-width-130px">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (isSelected) {
                onLogClick(
                  row.job_id,
                  row.job_name,
                  isTable ? table.table_id : null,
                  isTable ? table.table_name : null
                );
              }
            }}
            className={`dropdown-item notify-item ${
              !isSelected ? "disabled" : ""
            }`}
            style={!isSelected ? { opacity: 0.5, pointerEvents: "none" } : {}}
          >
            <p className="font-size-12 mb-0">
              <i className="fas fa-file pe-2"></i>Logs
            </p>
          </a>

          {!isTable && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (isSelected) {
                  if (isInProgressOrFailed) {
                    onViewProgress(row.job_id);
                  } else {
                    onViewClick(row.job_id);
                  }
                }
              }}
              className={`dropdown-item notify-item ${
                !isSelected ? "disabled" : ""
              }`}
              style={!isSelected ? { opacity: 0.5, pointerEvents: "none" } : {}}
            >
              <p className="font-size-12 mb-0">
                <i className="fas fa-file-lines pe-2"></i>
                {isInProgressOrFailed ? "View Progress" : "View"}
              </p>
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="migration-table-container">
      <div>
        <div>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="fw-bold mb-0">Data Migrations</h5>
              <div className="page-title-right">
                <ol className="breadcrumb m-0 p-0">
                  <li className="breadcrumb-item font-size-12">
                    <a
                      href="/home"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/home");
                      }}
                    >
                      Home
                    </a>
                  </li>
                  <li className="breadcrumb-item font-size-12 active ps-0 pe-0">
                    <span className="text-dark"> Data Migrations</span>
                  </li>
                </ol>
              </div>
            </div>
            <div className="d-flex justify-content-end align-items-end">
              <div className="d-flex gap-2 ms-auto">
                <CustomButton
                  onClick={handleEditClick}
                  text="Edit"
                  isEditEnabled={hasSelectedJob()} // Now checks for exactly one job selected
                />
                <CustomButton
                  onClick={() => {
                    setShowCreateDatabase(true);
                    setDatabaseStep(1);
                  }}
                  style={{ marginLeft: "10px" }}
                  text="Start Database Migration"
                  className="btn btn-primary custom-button"
                />
              </div>
            </div>
          </div>

          <div className="search-bar my-2 position-relative">
            <span
              className="ri-search-line position-absolute"
              style={{
                top: "50%",
                left: "10px",
                transform: "translateY(-50%)",
                fontSize: "16px",
              }}
            ></span>

            <input
              type="text"
              placeholder="Search"
              className="form-control highlighted-input colorfor-placeholder styled-input font-size-12 doc-search text-d-search pr-5"
              style={{ paddingLeft: "30px" }}
              value={searchQuery}
              // onChange={handleSearchChange}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <ul
          className="nav nav-tabs nav-tabs-custom nav-justified w-fit-content mb-1"
          role="tablist"
        >
          <li className="nav-item" role="presentation">
            <a
              className="nav-link active w-fit-content"
              data-bs-toggle="tab"
              href="#migrationjobs"
              role="tab"
              aria-selected="true"
              style={{
                backgroundColor: "#dcdfe3",
                borderRadius: "8px 8px 0 0",
                padding: "10px 20px",
                width: "140px",
                height: "29px",
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "21px",
                letterSpacing: "0%",
                color: "#354258",
                position: "relative",
                zIndex: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              <span className="d-sm-block white-space-nowrap">
                {/* Migration Jobs */}
                Data Migrations
                <span
                  style={{
                    display: "block",
                    height: "1px",
                    backgroundColor: "#354258",
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                />
              </span>
            </a>
          </li>
        </ul>

        <div className="tab-content tab-con">
          <div
            className="tab-pane active"
            id="migrationjobs"
            role="tabpanel"
            style={{
              maxHeight: "67vh",
              overflowY: "auto",
              position: "relative",
            }}
          >
            <table className="table">
              <thead className="table-head fw-bold">
                <tr>
                  <th style={{ ...cellStyle, fontSize: "16px" }}>
                    <input
                      type="checkbox"
                      ref={headerCheckboxRef}
                      className="custom-checkbox mb-0"
                      style={{ width: "17px" }}
                      checked={allChecked}
                      onChange={handleSelectAllChange}
                    />
                  </th>
                  <th style={{ ...cellStyle, width: "18%", fontSize: "11px" }}>
                    Migration Jobs
                  </th>
                  <th style={{ ...cellStyle, width: "25%", fontSize: "11px" }}>
                    Description
                  </th>
                  <th style={{ ...cellStyle, width: "10%", fontSize: "11px" }}>
                    Source
                  </th>
                  <th style={{ ...cellStyle, width: "10%", fontSize: "11px" }}>
                    Target
                  </th>
                  <th style={{ ...cellStyle, width: "15%", fontSize: "11px" }}>
                    Status
                  </th>
                  <th style={{ ...cellStyle, width: "15%", fontSize: "11px" }}>
                    Started
                  </th>
                  <th style={{ ...cellStyle, width: "18%", fontSize: "11px" }}>
                    Completed
                  </th>
                  <th style={{ ...cellStyle, width: "5%", fontSize: "11px" }}>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="scrollable-tbody" style={{ zIndex: 1 }}>
                {filteredData.length > 0 ? (
                  filteredData.map((row) => {
                    const jobKey = `job-${row.job_id}`;
                    return (
                      <React.Fragment key={row.job_id}>
                        <tr
                          style={{
                            ...cellStyle,
                            backgroundColor: checkedRows[jobKey]
                              ? "#F1F3FB"
                              : "transparent",
                          }}
                        >
                          <td className="pt-2" style={cellStyle}>
                            <div className="d-flex align-items-center">
                              <input
                                type="checkbox"
                                ref={(el) =>
                                  (jobCheckboxRefs.current[jobKey] = el)
                                }
                                checked={checkedRows[jobKey] || false}
                                className="custom-checkbox mr-2"
                                onChange={() =>
                                  handleCheckboxChange(row.job_id)
                                }
                                style={{ width: "17px" }}
                              />
                              <div className="ms-2 cursor-pointer">
                                {row.tables?.length > 0 && (
                                  <i
                                    className={`fas ${
                                      expandedRows[row.job_id]
                                        ? "fa-chevron-down"
                                        : "fa-chevron-right"
                                    } cursor-pointer me-2`}
                                    onClick={() => toggleRow(row.job_id)}
                                  />
                                )}
                              </div>
                            </div>
                          </td>
                          <td style={{ ...cellStyle, width: "18%" }}>
                            <TruncatedText
                              text={
                                row.job_name
                                  ? row.job_name.charAt(0).toUpperCase() +
                                    row.job_name.slice(1)
                                  : null
                              }
                              maxLength={12}
                              jobId={row.id}
                            />
                          </td>
                          <td style={{ ...cellStyle, width: "25%" }}>
                            <TruncatedText
                              text={
                                row.description
                                  ? row.description.charAt(0).toUpperCase() +
                                    row.description.slice(1)
                                  : null
                              }
                            />
                          </td>
                          <td style={{ ...cellStyle, width: "10%" }}>
                            {getPlatformDisplayName(row.source_db)}
                            {/* {row.source_db || "N/A"} */}
                          </td>
                          <td style={{ ...cellStyle, width: "10%" }}>
                            {getPlatformDisplayName(row.target_db)}
                          </td>
                          <td style={cellStyle}>
                            <div
                              className={`status-container ${
                                row.status === "In Progress"
                                  ? "executing"
                                  : row.status
                                  ? row.status.toLowerCase().replace(" ", "-")
                                  : "default"
                              }`}
                            >
                              {" "}
                              {row.status || "N/A"}
                            </div>
                          </td>
                          <td
                            style={{
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="ri-calendar-2-line"
                                style={{ fontSize: "14px", marginRight: "4px" }}
                              ></i>
                              <span> {row.started_at || "N/A"}</span>
                            </div>
                          </td>

                          <td
                            style={{
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="ri-calendar-2-line"
                                style={{ fontSize: "14px", marginRight: "4px" }}
                              ></i>
                              <span>{row.completed_at || "N/A"}</span>
                            </div>
                          </td>

                          <td style={{ textAlign: "center" }}>
                            {renderActions(row)}
                          </td>
                        </tr>

                        {expandedRows[row.job_id] &&
                          row.tables?.length > 0 &&
                          row.tables.map((table) => (
                            <tr
                              key={table.table_id}
                              style={{
                                ...cellStyle,
                                backgroundColor: checkedRows[
                                  `table-${table.table_id}`
                                ]
                                  ? "#F1F3FB"
                                  : "transparent",
                              }}
                              className="child-row"
                            >
                              <td
                                style={{
                                  ...cellStyle,
                                  width: "18%",
                                  maxWidth: "250px",
                                }}
                              >
                                <div
                                  className="d-flex align-items-center ps-1"
                                  style={{ gap: "8px" }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={
                                      checkedRows[`table-${table.table_id}`] ||
                                      false
                                    }
                                    className="custom-checkbox"
                                    onChange={() =>
                                      handleCheckboxChange(
                                        table.table_id,
                                        true,
                                        row.job_id
                                      )
                                    }
                                    style={{
                                      width: "17px",
                                      flexShrink: 0,
                                      alignSelf: "center",
                                    }}
                                  />
                                  <span
                                    style={{
                                      wordBreak: "break-word",
                                      whiteSpace: "normal",
                                      maxWidth: "250px",
                                    }}
                                  >
                                    {table.table_name || "N/A"}
                                  </span>
                                </div>
                              </td>
                              <td style={{ ...cellStyle, width: "25%" }}>
                                {table.schema_name || "N/A"}
                              </td>
                              <td style={{ ...cellStyle, width: "10%" }}>
                                {row.source_db || "N/A"}
                              </td>
                              <td style={{ ...cellStyle, width: "10%" }}>
                                {row.target_db || "N/A"}
                              </td>
                              <td style={cellStyle}>
                                <div
                                  className={`status-container ${
                                    table.status === "In Progress"
                                      ? "executing"
                                      : table.status
                                      ? table.status
                                          .toLowerCase()
                                          .replace(" ", "-")
                                      : "default"
                                  }`}
                                >
                                  {table.status || "N/A"}
                                </div>
                              </td>
                              <td
                                style={{
                                  whiteSpace: "nowrap",
                                  verticalAlign: "middle",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <i
                                    className="ri-calendar-2-line"
                                    style={{
                                      fontSize: "14px",
                                      marginRight: "4px",
                                    }}
                                  ></i>
                                  <span>{table.table_started_at || "N/A"}</span>
                                </div>
                              </td>

                              <td
                                style={{
                                  whiteSpace: "nowrap",
                                  verticalAlign: "middle",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <i
                                    className="ri-calendar-2-line"
                                    style={{
                                      fontSize: "14px",
                                      marginRight: "4px",
                                    }}
                                  ></i>
                                  <span>
                                    {table.table_completed_at || "N/A"}
                                  </span>
                                </div>
                              </td>

                              <td style={{ textAlign: "center" }}>
                                {renderActions(row, true, table)}
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={9}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "calc(clamp(58vh, 62vh + 2vw, 80vh) - 40px)",
                          textAlign: "center",
                        }}
                      >
                        <img
                          src={norecords || "/placeholder.svg"}
                          alt="No Records"
                          style={{ width: "220px" }}
                        />
                        <h4
                          className="fw-bold pt-3"
                          style={{ fontSize: "18px" }}
                        >
                          No migration jobs found
                        </h4>
                        <p>
                          Looks like there are no migration jobs yet. Ready to
                          get started?
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ListOfMigrationTable;
